import { formatDistanceToNow } from 'date-fns';
import { ChevronDown, Download, Plus, RefreshCw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { AddServerDialog } from '@/components/fleet/add-server-dialog';
import { ConfirmDialog } from '@/components/fleet/confirm-dialog';
import { FilterBar } from '@/components/fleet/filter-bar';
import { KpiStrip } from '@/components/fleet/kpi-strip';
import { ProvisionVpnWizard } from '@/components/fleet/provision-vpn-wizard/provision-vpn-wizard';
import { ServerTable } from '@/components/fleet/server-table';
import {
  EmptyState,
  ErrorState,
  TableSkeleton,
  useMinimumVisible,
} from '@/components/fleet/states';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useBulkProvisionServers,
  type BulkProvisionResponse,
} from '@/hooks/use-bulk-provision-servers';
import { useFleetFilters } from '@/hooks/use-fleet-filters';
import { useFleetServers } from '@/hooks/use-fleet-servers';
import { downloadCsv, serversToCsv } from '@/lib/csv';

const PAGE_SIZES = [25, 50, 100, 200];

function LastUpdated({ updatedAt }: { updatedAt: number }): JSX.Element {
  const [, tick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 5_000);
    return () => clearInterval(id);
  }, []);

  if (!updatedAt) return <span />;
  return (
    <span className="font-mono text-xs text-muted-foreground">
      updated {formatDistanceToNow(updatedAt, { addSuffix: true })}
    </span>
  );
}

/** `Add ▾` dropdown — subsumes the "Add server" dialog. */
function AddMenu(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <Plus className="h-4 w-4" aria-hidden />
        Add
        <ChevronDown className="h-3.5 w-3.5 ml-1" aria-hidden />
      </Button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 min-w-[180px] rounded-md border border-border bg-card shadow-panel">
          {[
            { label: 'Register existing', onClick: () => { setOpen(false); setDialogOpen(true); } },
          ].map((item) => (
            <button
              key={item.label}
              type="button"
              className="flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-accent"
              onClick={item.onClick}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}

      <AddServerDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </div>
  );
}

export function FleetListPage(): JSX.Element {
  const filters = useFleetFilters();
  const { query, setPage, setPageSize, reset } = filters;
  const { data, isLoading, isError, error, isFetching, dataUpdatedAt, refetch } =
    useFleetServers(query);

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkProvisionResponse | null>(null);
  const bulkProvision = useBulkProvisionServers();

  const page = query.page ?? 1;
  const pageSize = query.page_size ?? 50;
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  const showSkeleton = useMinimumVisible(isLoading);
  const showStaleBanner = isError && data !== undefined;
  const isPageOutOfRange = data !== undefined && data.items.length === 0 && page > 1;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
            Fleet · Servers
          </p>
          <h1 className="text-lg font-semibold tracking-tight text-foreground">Servers</h1>
          <p className="text-sm text-muted-foreground">
            Every VPN server and exit node, with live state.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {showStaleBanner && (
            <span className="inline-flex items-center gap-1.5 rounded border border-status-degraded/30 bg-status-degraded/10 px-2 py-1 font-mono text-[11px] uppercase tracking-wide text-status-degraded">
              Connection issue — data may be stale
            </span>
          )}
          <LastUpdated updatedAt={dataUpdatedAt} />
          <Button
            variant="ghost"
            size="sm"
            disabled={!data || data.items.length === 0}
            onClick={() => {
              if (!data) return;
              const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
              downloadCsv(serversToCsv(data.items), `fleet-${stamp}.csv`);
            }}
          >
            <Download className="h-4 w-4" aria-hidden />
            Export
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setBulkOpen(true)}
            disabled={bulkProvision.isPending}
          >
            <RefreshCw className="h-4 w-4" aria-hidden />
            Re-provision all
          </Button>
          <ProvisionVpnWizard />
          <AddMenu />
        </div>
      </div>

      {/* KPI strip */}
      <KpiStrip />

      {/* Filter bar + table */}
      <div>
        <FilterBar />

        <div className="pt-4">
          {showSkeleton && <TableSkeleton />}

          {!showSkeleton && isError && !data && (
            <ErrorState error={error} onRetry={() => void refetch()} />
          )}

          {!showSkeleton && isPageOutOfRange && (
            <EmptyState
              message={`Page ${page} is out of range — the fleet has fewer pages now.`}
              action={
                <Button variant="outline" size="sm" onClick={() => setPage(1)}>
                  Go to page 1
                </Button>
              }
            />
          )}

          {!showSkeleton && data && data.items.length === 0 && !isPageOutOfRange && (
            <EmptyState
              message="No servers match these filters."
              action={
                <Button variant="outline" size="sm" onClick={reset}>
                  Clear filters
                </Button>
              }
            />
          )}

          {!showSkeleton && data && data.items.length > 0 && (
            <>
              <ServerTable rows={data.items} filters={filters} />

              {/* Pagination — single line */}
              <div className="flex items-center justify-between gap-3 pt-4 text-sm">
                <span className="font-mono text-xs tabular-nums text-muted-foreground">
                  {total} server{total === 1 ? '' : 's'}
                  {isFetching && <span className="ml-2">refreshing…</span>}
                </span>

                <div className="flex items-center gap-3">
                  <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                    <SelectTrigger className="h-8 w-[72px] font-mono text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZES.map((size) => (
                        <SelectItem key={size} value={String(size)}>
                          {size}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="font-mono text-xs tabular-nums text-muted-foreground">
                    {page} / {pageCount}
                  </span>
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                    ‹
                  </Button>
                  <Button variant="outline" size="sm" disabled={page >= pageCount} onClick={() => setPage(page + 1)}>
                    ›
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        title="Re-provision ALL servers"
        description={
          <div className="space-y-2">
            <p>
              This re-dispatches the provisioning playbook for{' '}
              <span className="font-semibold">every server except decommissioned ones</span>, using
              their stored SSH credentials and recorded protocols. Each runs the correct playbook for
              its role.
            </p>
            <p className="text-status-degraded">
              Live VPN nodes will re-run config/hardening and may briefly disrupt connected users.
              Dispatch is throttled (5 at a time) and Semaphore queues the rest.
            </p>
          </div>
        }
        confirmLabel="Re-provision all"
        variant="destructive"
        reasonRequired
        confirmPhrase="re-provision all"
        pending={bulkProvision.isPending}
        onConfirm={(reason) => {
          bulkProvision.mutate(
            { reason },
            {
              onSuccess: (res) => {
                setBulkOpen(false);
                setBulkResult(res);
                const msg = `Re-provision dispatched: ${res.dispatched} of ${res.total} (skipped ${res.skipped}, failed ${res.failed})`;
                if (res.failed > 0) toast.warning(msg);
                else toast.success(msg);
              },
              onError: (err) => toast.error(`Bulk re-provision failed: ${err.message}`),
            },
          );
        }}
      />

      <Dialog open={bulkResult !== null} onOpenChange={(o) => !o && setBulkResult(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Re-provision results</DialogTitle>
            <DialogDescription>
              {bulkResult
                ? `${bulkResult.dispatched} of ${bulkResult.total} dispatched · ${bulkResult.skipped} skipped · ${bulkResult.failed} failed`
                : ''}
            </DialogDescription>
          </DialogHeader>

          {bulkResult && bulkResult.failed + bulkResult.skipped > 0 ? (
            <div className="max-h-72 space-y-1 overflow-y-auto">
              {bulkResult.results
                .filter((r) => r.status !== 'dispatched')
                .map((r) => (
                  <div
                    key={r.name}
                    className="flex items-start justify-between gap-3 rounded border border-border/60 px-2 py-1.5 text-xs"
                  >
                    <span className="font-mono">{r.name}</span>
                    <span
                      className={
                        r.status === 'failed'
                          ? 'text-right text-status-degraded'
                          : 'text-right text-muted-foreground'
                      }
                    >
                      {r.status}
                      {r.error ? ` — ${r.error}` : ''}
                    </span>
                  </div>
                ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              All eligible servers were dispatched. Watch the status badges for progress.
            </p>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkResult(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
